import { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Loader2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  ArrowLeftRight,
  ExternalLink,
  Eye,
  EyeOff,
  Unplug,
  Shield,
  FolderSync,
  Download,
} from 'lucide-react';
import {
  opSyncApi,
  type OpConfig,
  type OpSyncMapping,
  type TestConnectionResponse,
  type ProjectSyncStatus,
  type OpProjectItem,
} from '@/lib/api';

// ── Status Mapping Types ─────────────────────────────────────

interface StatusMappingEntry {
  key: string;
  label: string;
  opId: number;
}

const DEFAULT_STATUS_ENTRIES: StatusMappingEntry[] = [
  { key: 'todo', label: 'Todo', opId: 15 },
  { key: 'in_progress', label: 'In Progress', opId: 7 },
  { key: 'in_review', label: 'In Review', opId: 17 },
  { key: 'done', label: 'Done', opId: 19 },
  { key: 'cancelled', label: 'Cancelled', opId: 20 },
];

function parseStatusMapping(json: string): StatusMappingEntry[] {
  try {
    const parsed = JSON.parse(json) as Record<string, number>;
    return DEFAULT_STATUS_ENTRIES.map((entry) => ({
      ...entry,
      opId: parsed[entry.key] ?? entry.opId,
    }));
  } catch {
    return [...DEFAULT_STATUS_ENTRIES];
  }
}

function serializeStatusMapping(entries: StatusMappingEntry[]): string {
  const obj: Record<string, number> = {};
  for (const e of entries) {
    obj[e.key] = e.opId;
  }
  return JSON.stringify(obj);
}

// ── Component ────────────────────────────────────────────────

export function OpSyncSettings() {
  // Config state
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<OpConfig | null>(null);
  const [baseUrl, setBaseUrl] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [showToken, setShowToken] = useState(false);

  // New fields
  const [vkBaseUrl, setVkBaseUrl] = useState('http://localhost:3002');
  const [slackSigningSecret, setSlackSigningSecret] = useState('');
  const [showSigningSecret, setShowSigningSecret] = useState(false);
  const [statusEntries, setStatusEntries] = useState<StatusMappingEntry[]>(
    () => [...DEFAULT_STATUS_ENTRIES]
  );

  // Mappings state
  const [mappings, setMappings] = useState<OpSyncMapping[]>([]);
  const [mappingsLoading, setMappingsLoading] = useState(false);

  // Project sync selection state
  const [projectStatuses, setProjectStatuses] = useState<ProjectSyncStatus[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [syncingProjects, setSyncingProjects] = useState<Set<string>>(new Set());
  const [resyncingProjects, setResyncingProjects] = useState<Set<string>>(new Set());

  // Import from OP state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [opProjects, setOpProjects] = useState<OpProjectItem[]>([]);
  const [opProjectsLoading, setOpProjectsLoading] = useState(false);
  const [importingProject, setImportingProject] = useState<number | null>(null);

  // UI state
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestConnectionResponse | null>(
    null
  );

  // Load config on mount
  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const cfg = await opSyncApi.getConfig();
      setConfig(cfg);
      setBaseUrl(cfg.base_url);
      setApiToken(cfg.api_token);
      setEnabled(cfg.enabled !== 0);
      setVkBaseUrl(cfg.vk_base_url || 'http://localhost:3002');
      setSlackSigningSecret(cfg.slack_signing_secret || '');
      setStatusEntries(parseStatusMapping(cfg.status_mapping || '{}'));
    } catch (err) {
      setError('Failed to load OpenProject configuration');
      console.error('Failed to load OP config:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMappings = useCallback(async () => {
    try {
      setMappingsLoading(true);
      const data = await opSyncApi.getMappings();
      setMappings(data);
    } catch (err) {
      console.error('Failed to load mappings:', err);
    } finally {
      setMappingsLoading(false);
    }
  }, []);

  const loadProjectStatuses = useCallback(async () => {
    try {
      setProjectsLoading(true);
      const data = await opSyncApi.listProjectSyncStatus();
      setProjectStatuses(data);
    } catch (err) {
      console.error('Failed to load project sync statuses:', err);
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  const handleToggleProjectSync = async (project: ProjectSyncStatus) => {
    const projectId = project.id;
    setSyncingProjects((prev) => new Set(prev).add(projectId));
    setError(null);

    try {
      if (project.synced) {
        // Unsync: confirm first
        const confirmed = window.confirm(
          `Are you sure you want to unsync "${project.name}" from OpenProject?\n\nThis will remove the sync mapping. Data on OpenProject will be preserved.`
        );
        if (!confirmed) {
          setSyncingProjects((prev) => {
            const next = new Set(prev);
            next.delete(projectId);
            return next;
          });
          return;
        }
        await opSyncApi.unsyncProject(projectId);
        setSuccess(`"${project.name}" has been unsynced from OpenProject`);
      } else {
        // Sync
        await opSyncApi.syncProject(projectId);
        setSuccess(`"${project.name}" has been synced to OpenProject`);
      }
      // Reload both project statuses and mappings
      await Promise.all([loadProjectStatuses(), loadMappings()]);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err?.message || `Failed to ${project.synced ? 'unsync' : 'sync'} project`);
    } finally {
      setSyncingProjects((prev) => {
        const next = new Set(prev);
        next.delete(projectId);
        return next;
      });
    }
  };

  const handleResyncBoard = async (project: ProjectSyncStatus) => {
    const projectId = project.id;
    setResyncingProjects((prev) => new Set(prev).add(projectId));
    setError(null);

    try {
      const result = await opSyncApi.resyncBoard(projectId);
      setSuccess(result.message);
      // Reload mappings to reflect updated status_mapping
      await loadMappings();
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err?.message || 'Failed to resync board');
    } finally {
      setResyncingProjects((prev) => {
        const next = new Set(prev);
        next.delete(projectId);
        return next;
      });
    }
  };

  const loadOpProjects = useCallback(async () => {
    try {
      setOpProjectsLoading(true);
      const data = await opSyncApi.listOpProjects();
      setOpProjects(data);
    } catch (err) {
      console.error('Failed to load OP projects:', err);
    } finally {
      setOpProjectsLoading(false);
    }
  }, []);

  const handleImportProject = async (opProject: OpProjectItem) => {
    setImportingProject(opProject.id);
    setError(null);

    try {
      const result = await opSyncApi.importProject(opProject.id);
      setSuccess(
        `Imported "${result.project_name}" with ${result.tasks_imported} task(s)${result.tasks_skipped > 0 ? ` (${result.tasks_skipped} skipped)` : ''
        }`
      );
      setShowImportDialog(false);
      // Reload data
      await Promise.all([loadProjectStatuses(), loadMappings(), loadOpProjects()]);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err?.message || 'Failed to import project');
    } finally {
      setImportingProject(null);
    }
  };

  useEffect(() => {
    loadConfig();
    loadMappings();
    loadProjectStatuses();
  }, [loadConfig, loadMappings, loadProjectStatuses]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    setTestResult(null);

    try {
      const updated = await opSyncApi.updateConfig({
        base_url: baseUrl,
        api_token: apiToken,
        enabled,
        status_mapping: serializeStatusMapping(statusEntries),
        vk_base_url: vkBaseUrl,
        slack_signing_secret: slackSigningSecret,
      });
      setConfig(updated);
      setSuccess('Configuration saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to save configuration');
      console.error('Failed to save OP config:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const result = await opSyncApi.testConnection({
        base_url: baseUrl,
        api_token: apiToken,
      });
      setTestResult(result);
    } catch (err) {
      setTestResult({
        success: false,
        message: 'Failed to reach the server',
      });
      console.error('Connection test failed:', err);
    } finally {
      setTesting(false);
    }
  };

  const updateStatusEntry = (index: number, opId: number) => {
    setStatusEntries((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], opId };
      return updated;
    });
  };

  const hasUnsavedChanges = (() => {
    if (!config) return false;
    if (baseUrl !== config.base_url) return true;
    if (apiToken !== config.api_token) return true;
    if (enabled !== (config.enabled !== 0)) return true;
    if (vkBaseUrl !== (config.vk_base_url || 'http://localhost:3002')) return true;
    if (slackSigningSecret !== (config.slack_signing_secret || '')) return true;
    const currentMapping = serializeStatusMapping(statusEntries);
    if (currentMapping !== (config.status_mapping || '{}')) return true;
    return false;
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading OpenProject configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert variant="success">
          <AlertDescription className="font-medium">{success}</AlertDescription>
        </Alert>
      )}

      {/* Connection Configuration */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Unplug className="h-5 w-5" />
                Connection Settings
              </CardTitle>
              <CardDescription>
                Configure the connection to your OpenProject instance for
                two-way synchronization of projects and tasks.
              </CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="op-sync-enabled" className="text-sm font-medium">
                {enabled ? 'Enabled' : 'Disabled'}
              </Label>
              <Switch
                id="op-sync-enabled"
                checked={enabled}
                onCheckedChange={setEnabled}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="op-base-url">OpenProject Base URL</Label>
            <Input
              id="op-base-url"
              type="url"
              placeholder="http://localhost:3000"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              The URL of your OpenProject instance (e.g.,{' '}
              <code className="text-xs bg-muted px-1 py-0.5 rounded">
                https://openproject.example.com
              </code>
              )
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="op-api-token">API Token</Label>
            <div className="relative">
              <Input
                id="op-api-token"
                type={showToken ? 'text' : 'password'}
                placeholder="Enter your OpenProject API token"
                value={apiToken}
                onChange={(e) => setApiToken(e.target.value)}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowToken(!showToken)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showToken ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            <p className="text-sm text-muted-foreground">
              Generate an API token in OpenProject under{' '}
              <span className="font-medium">
                My Account → Access tokens
              </span>
              .{' '}
              {baseUrl && (
                <a
                  href={`${baseUrl.replace(/\/$/, '')}/my/access_token`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  Open token page
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="vk-base-url">VibeKanban Frontend URL</Label>
            <Input
              id="vk-base-url"
              type="url"
              placeholder="http://localhost:3002"
              value={vkBaseUrl}
              onChange={(e) => setVkBaseUrl(e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              Used for generating task links in Slack/Telegram/Discord
              notifications.
            </p>
          </div>

          {/* Test Connection */}
          <div className="flex items-center gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleTestConnection}
              disabled={testing || !baseUrl || !apiToken}
              className="gap-2"
            >
              {testing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Test Connection
            </Button>

            {testResult && (
              <div
                className={`flex items-center gap-2 text-sm ${testResult.success ? 'text-green-600' : 'text-destructive'
                  }`}
              >
                {testResult.success ? (
                  <CheckCircle2 className="h-4 w-4" />
                ) : (
                  <XCircle className="h-4 w-4" />
                )}
                {testResult.message}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Project Sync Selection */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FolderSync className="h-5 w-5" />
                Project Sync Selection
              </CardTitle>
              <CardDescription>
                Choose which projects should be synchronized to OpenProject.
                Only selected projects will have their tasks synced.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadProjectStatuses}
              disabled={projectsLoading}
              className="gap-2"
            >
              {projectsLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!enabled ? (
            <div className="text-center py-6">
              <FolderSync className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">
                Enable OpenProject sync above to manage project synchronization.
              </p>
            </div>
          ) : projectsLoading && projectStatuses.length === 0 ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              <span className="text-sm text-muted-foreground">
                Loading projects...
              </span>
            </div>
          ) : projectStatuses.length === 0 ? (
            <div className="text-center py-6">
              <FolderSync className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">
                No projects found. Create a project first to enable sync.
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {projectStatuses.map((project) => {
                const isSyncing = syncingProjects.has(project.id);
                return (
                  <div
                    key={project.id}
                    className="flex items-center justify-between py-3 px-3 rounded-lg hover:bg-muted/50 transition-colors border border-transparent hover:border-border"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm truncate">
                            {project.name}
                          </span>
                          {project.synced ? (
                            <Badge variant="default" className="text-xs shrink-0 bg-green-600 hover:bg-green-700">
                              Synced
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs shrink-0">
                              Not synced
                            </Badge>
                          )}
                        </div>
                        {project.synced && project.op_project_id && (
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-muted-foreground">
                              OP Project #{project.op_project_id}
                            </span>
                            {project.op_identifier && (
                              <span className="text-xs text-muted-foreground">
                                ({project.op_identifier})
                              </span>
                            )}
                            {baseUrl && (
                              <a
                                href={`${baseUrl.replace(/\/$/, '')}/projects/${project.op_identifier || project.op_project_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                              >
                                Open
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {project.synced && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleResyncBoard(project)}
                          disabled={resyncingProjects.has(project.id) || isSyncing || !enabled}
                          className="gap-1.5 text-xs h-7"
                        >
                          {resyncingProjects.has(project.id) ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <RefreshCw className="h-3 w-3" />
                          )}
                          {resyncingProjects.has(project.id) ? 'Resyncing...' : 'Resync Board'}
                        </Button>
                      )}
                      {isSyncing ? (
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      ) : null}
                      <Switch
                        checked={project.synced}
                        onCheckedChange={() => handleToggleProjectSync(project)}
                        disabled={isSyncing || !enabled}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Import from OpenProject */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Download className="h-5 w-5" />
                Import from OpenProject
              </CardTitle>
              <CardDescription>
                Import an existing OpenProject project and its work packages
                into VibeKanban. Tasks will be created with sync mappings for
                bidirectional sync.
              </CardDescription>
            </div>
            <Button
              variant="default"
              size="sm"
              onClick={() => {
                setShowImportDialog(!showImportDialog);
                if (!showImportDialog) loadOpProjects();
              }}
              disabled={!enabled}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Import Project
            </Button>
          </div>
        </CardHeader>
        {showImportDialog && (
          <CardContent>
            {!enabled ? (
              <div className="text-center py-6">
                <Download className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">
                  Enable OpenProject sync above to import projects.
                </p>
              </div>
            ) : opProjectsLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                <span className="text-sm text-muted-foreground">
                  Loading OpenProject projects...
                </span>
              </div>
            ) : opProjects.length === 0 ? (
              <div className="text-center py-6">
                <Download className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
                <p className="text-sm text-muted-foreground">
                  No projects found on OpenProject.
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-muted-foreground">
                    {opProjects.length} project(s) on OpenProject
                    {' · '}
                    <span className="text-green-600 dark:text-green-400">
                      {opProjects.filter((op) =>
                        mappings.some(
                          (m) => m.op_entity_type === 'project' && m.op_entity_id === op.id
                        )
                      ).length} synced
                    </span>
                  </p>
                </div>
                {opProjects
                  .filter((op) => op.active)
                  .sort((a, b) => {
                    // Show synced first, then unsynced
                    const aSynced = mappings.some(
                      (m) => m.op_entity_type === 'project' && m.op_entity_id === a.id
                    );
                    const bSynced = mappings.some(
                      (m) => m.op_entity_type === 'project' && m.op_entity_id === b.id
                    );
                    if (aSynced && !bSynced) return -1;
                    if (!aSynced && bSynced) return 1;
                    return a.name.localeCompare(b.name);
                  })
                  .map((op) => {
                    const syncMapping = mappings.find(
                      (m) => m.op_entity_type === 'project' && m.op_entity_id === op.id
                    );
                    const isSynced = !!syncMapping;
                    const isImporting = importingProject === op.id;
                    // Find the PAI project name from projectStatuses
                    const linkedPaiProject = isSynced
                      ? projectStatuses.find((p) => p.id === syncMapping?.vk_entity_id)
                      : null;

                    return (
                      <div
                        key={op.id}
                        className={`flex items-center justify-between py-3 px-3 rounded-lg transition-colors border ${isSynced
                            ? 'border-green-200 bg-green-50/50 dark:border-green-900/50 dark:bg-green-950/20'
                            : 'border-transparent hover:bg-muted/50 hover:border-border'
                          }`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm truncate">
                              {op.name}
                            </span>
                            <Badge
                              variant="outline"
                              className="text-xs shrink-0"
                            >
                              #{op.id}
                            </Badge>
                            {isSynced && (
                              <Badge className="text-xs shrink-0 bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 border-green-200 dark:border-green-800">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Synced
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-muted-foreground">
                              {op.identifier}
                            </span>
                            {isSynced && linkedPaiProject && (
                              <span className="text-xs text-green-600 dark:text-green-400">
                                → PAI: {linkedPaiProject.name}
                              </span>
                            )}
                            {isSynced && !linkedPaiProject && syncMapping && (
                              <span className="text-xs text-green-600 dark:text-green-400">
                                → PAI #{syncMapping.vk_entity_id.slice(0, 8)}…
                              </span>
                            )}
                          </div>
                        </div>
                        {isSynced ? (
                          <Badge variant="secondary" className="text-xs shrink-0 gap-1">
                            <ArrowLeftRight className="h-3 w-3" />
                            Linked
                          </Badge>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={isImporting || importingProject !== null}
                            onClick={() => handleImportProject(op)}
                            className="gap-2 shrink-0"
                          >
                            {isImporting ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                            {isImporting ? 'Importing...' : 'Import'}
                          </Button>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Slack Security */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Slack Security
          </CardTitle>
          <CardDescription>
            Verify incoming Slack requests using a signing secret to prevent
            unauthorized access to your Slack integration endpoints.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="slack-signing-secret">Slack Signing Secret</Label>
            <div className="relative">
              <Input
                id="slack-signing-secret"
                type={showSigningSecret ? 'text' : 'password'}
                placeholder="Enter your Slack app signing secret"
                value={slackSigningSecret}
                onChange={(e) => setSlackSigningSecret(e.target.value)}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowSigningSecret(!showSigningSecret)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showSigningSecret ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            <p className="text-sm text-muted-foreground">
              Find this in your Slack app settings under{' '}
              <span className="font-medium">
                Basic Information → App Credentials → Signing Secret
              </span>
              . When set, all incoming Slack requests are verified with
              HMAC-SHA256. Leave empty to skip verification.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Status Mapping */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5" />
            Status Mapping
          </CardTitle>
          <CardDescription>
            Map VibeKanban task statuses to OpenProject status IDs. These IDs
            must match your OpenProject instance configuration.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-x-4 gap-y-3 items-center">
              <p className="text-xs font-medium text-muted-foreground">
                VibeKanban Status
              </p>
              <p className="text-xs text-muted-foreground text-center">→</p>
              <p className="text-xs font-medium text-muted-foreground">
                OpenProject Status ID
              </p>

              {statusEntries.map((entry, idx) => (
                <>
                  <div
                    key={`label-${entry.key}`}
                    className="flex items-center gap-2"
                  >
                    <Badge variant="secondary" className="text-xs">
                      {entry.label}
                    </Badge>
                  </div>
                  <span
                    key={`arrow-${entry.key}`}
                    className="text-muted-foreground text-center"
                  >
                    ↔
                  </span>
                  <Input
                    key={`input-${entry.key}`}
                    type="number"
                    min={1}
                    value={entry.opId}
                    onChange={(e) =>
                      updateStatusEntry(idx, parseInt(e.target.value) || 1)
                    }
                    className="w-24 h-8 text-sm"
                  />
                </>
              ))}
            </div>
            <p className="text-xs text-muted-foreground pt-2">
              To find status IDs, visit{' '}
              {baseUrl ? (
                <a
                  href={`${baseUrl.replace(/\/$/, '')}/api/v3/statuses`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:underline"
                >
                  {baseUrl.replace(/\/$/, '')}/api/v3/statuses
                  <ExternalLink className="h-3 w-3" />
                </a>
              ) : (
                <code className="text-xs bg-muted px-1 py-0.5 rounded">
                  /api/v3/statuses
                </code>
              )}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Sync Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ArrowLeftRight className="h-5 w-5" />
            Synchronization Behavior
          </CardTitle>
          <CardDescription>
            How data flows between VibeKanban and OpenProject.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* VK → OP */}
              <div className="border rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="default" className="text-xs">
                    VK → OP
                  </Badge>
                  <span className="text-sm font-medium">
                    VibeKanban to OpenProject
                  </span>
                </div>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-green-500 shrink-0" />
                    Create project → OP project + Kanban board
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-green-500 shrink-0" />
                    Create task → OP work package
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-green-500 shrink-0" />
                    Update task (title, description, status)
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-green-500 shrink-0" />
                    Auto-retry on failure (3 attempts, exponential backoff)
                  </li>
                </ul>
                <p className="text-xs text-muted-foreground italic">
                  Triggered immediately (event-driven)
                </p>
              </div>

              {/* OP → VK */}
              <div className="border rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="text-xs">
                    OP → VK
                  </Badge>
                  <span className="text-sm font-medium">
                    OpenProject to VibeKanban
                  </span>
                </div>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-green-500 shrink-0" />
                    Status changes synced back to VK
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-green-500 shrink-0" />
                    Title / description changes
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-green-500 shrink-0" />
                    WP deleted in OP → task deleted in VK
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-green-500 shrink-0" />
                    Import OP project → VK project + tasks
                  </li>
                </ul>
                <p className="text-xs text-muted-foreground italic">
                  Polled every 5 seconds
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sync Mappings */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Sync Mappings</CardTitle>
              <CardDescription>
                Entities currently linked between VibeKanban and OpenProject.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadMappings}
              disabled={mappingsLoading}
              className="gap-2"
            >
              {mappingsLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {mappingsLoading && mappings.length === 0 ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              <span className="text-sm text-muted-foreground">
                Loading mappings...
              </span>
            </div>
          ) : mappings.length === 0 ? (
            <div className="text-center py-6">
              <ArrowLeftRight className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">
                No sync mappings yet. Create a project or task with sync enabled
                to see mappings here.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2 px-3 font-medium text-muted-foreground">
                      Type
                    </th>
                    <th className="py-2 px-3 font-medium text-muted-foreground">
                      VK Entity
                    </th>
                    <th className="py-2 px-3 font-medium text-muted-foreground">
                      OP Entity
                    </th>
                    <th className="py-2 px-3 font-medium text-muted-foreground">
                      Last Synced
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {mappings.map((m) => (
                    <tr
                      key={m.id}
                      className="border-b last:border-0 hover:bg-muted/50 transition-colors"
                    >
                      <td className="py-2 px-3">
                        <Badge
                          variant={
                            m.vk_entity_type === 'project'
                              ? 'default'
                              : 'secondary'
                          }
                          className="text-xs"
                        >
                          {m.vk_entity_type}
                        </Badge>
                      </td>
                      <td className="py-2 px-3 font-mono text-xs truncate max-w-[160px]">
                        {m.vk_entity_id}
                      </td>
                      <td className="py-2 px-3">
                        <span className="font-mono text-xs">
                          {m.op_entity_type}#{m.op_entity_id}
                        </span>
                        {m.op_identifier && (
                          <span className="ml-1 text-xs text-muted-foreground">
                            ({m.op_identifier})
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-xs text-muted-foreground">
                        {new Date(m.synced_at).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sticky Save Button */}
      <div className="sticky bottom-0 z-10 bg-background/80 backdrop-blur-sm border-t py-4">
        <div className="flex items-center justify-between">
          {hasUnsavedChanges ? (
            <span className="text-sm text-muted-foreground">
              You have unsaved changes
            </span>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={loadConfig}
              disabled={!hasUnsavedChanges || saving}
            >
              Discard
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasUnsavedChanges || saving}
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
